import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { Building2, Users, Calendar, Utensils, CreditCard, Sparkles, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';

export default function BulkRFPFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  
  const [formData, setFormData] = useState({
    // Company Information
    company_name: '',
    company_industry: 'technology',
    gst_number: '',
    decision_maker_name: user?.name || '',
    decision_maker_email: '',
    decision_maker_phone: '',
    finance_contact_name: '',
    finance_contact_email: '',
    previous_mice_experience: 'first_time',
    
    // Event Details
    event_name: '',
    event_type: 'conference',
    event_description: '',
    min_attendees: 50,
    max_attendees: 100,
    preferred_cities: [],
    primary_check_in: null,
    primary_check_out: null,
    alternate_check_in: null,
    alternate_check_out: null,
    event_duration_days: 2,
    is_recurring_event: false,
    recurring_frequency: '',
    
    // Accommodation
    room_categories: [{ category: 'deluxe', count: 50, occupancy: 'double' }],
    total_rooms_required: 50,
    early_checkin_required: false,
    late_checkout_required: false,
    special_room_requirements: '',
    
    // Venue
    session_types: ['plenary'],
    main_venue_capacity: 100,
    seating_arrangement: 'theatre',
    breakout_rooms_count: 0,
    av_requirements: ['projector', 'microphones'],
    stage_required: false,
    exhibition_space_sqft: 0,
    
    // Event Elements Sheet
    event_elements: {
      sound: { required: false, specs: '', budget: '' },
      lighting: { required: false, specs: '', budget: '' },
      led_screens: { required: false, specs: '', budget: '' },
      stage: { required: false, specs: '', budget: '' },
      branding: { required: false, specs: '', budget: '' },
      backdrop: { required: false, specs: '', budget: '' },
      furniture: { required: false, specs: '', budget: '' },
      decor: { required: false, specs: '', budget: '' },
      signage: { required: false, specs: '', budget: '' },
      photo_booth: { required: false, specs: '', budget: '' },
      live_streaming: { required: false, specs: '', budget: '' },
      interpretation: { required: false, specs: '', budget: '' },
    },
    
    // F&B
    meal_plans: ['breakfast', 'lunch', 'dinner'],
    cuisine_preferences: ['indian', 'continental'],
    vegetarian_percentage: 30,
    special_dietary_needs: '',
    cocktail_dinner_required: false,
    outside_catering_allowed: false,
    
    // Budget
    total_budget_min: 500000,
    total_budget_max: 1000000,
    payment_terms_preference: 'advance_50_balance_before',
    need_installment_plan: false,
    
    // Additional Services
    airport_transfers_required: false,
    team_building_activities: false,
    spa_wellness_packages: false,
    gifts_welcome_kits: false,
    photography_videography: false,
    entertainment_required: false,
    special_requests: ''
  });

  const sections = [
    { title: 'Company Information', icon: Building2 },
    { title: 'Event Details', icon: Calendar },
    { title: 'Accommodation', icon: Building2 },
    { title: 'Venue Requirements', icon: Users },
    { title: 'Event Elements Sheet', icon: Sparkles },
    { title: 'Food & Beverage', icon: Utensils },
    { title: 'Budget & Payment', icon: CreditCard },
    { title: 'Additional Services', icon: Sparkles }
  ];

  const eventElementsList = [
    { key: 'sound', label: 'Sound System', icon: '🔊', desc: 'PA system, speakers, mixers' },
    { key: 'lighting', label: 'Lighting', icon: '💡', desc: 'Stage lights, ambiance, moving heads' },
    { key: 'led_screens', label: 'LED Screens', icon: '📺', desc: 'Video walls, monitors, displays' },
    { key: 'stage', label: 'Stage Setup', icon: '🎭', desc: 'Stage, podium, risers' },
    { key: 'branding', label: 'Branding Elements', icon: '🏷️', desc: 'Banners, standees, collaterals' },
    { key: 'backdrop', label: 'Backdrop/Set Design', icon: '🎨', desc: 'Custom backdrop, set construction' },
    { key: 'furniture', label: 'Event Furniture', icon: '🪑', desc: 'Tables, chairs, lounge seating' },
    { key: 'decor', label: 'Décor & Florals', icon: '💐', desc: 'Floral arrangements, centerpieces' },
    { key: 'signage', label: 'Signage & Wayfinding', icon: '🪧', desc: 'Direction signs, name plates' },
    { key: 'photo_booth', label: 'Photo/Video Booth', icon: '📸', desc: '360 booth, photo booth' },
    { key: 'live_streaming', label: 'Live Streaming', icon: '📡', desc: 'Virtual broadcast, hybrid setup' },
    { key: 'interpretation', label: 'Interpretation', icon: '🎧', desc: 'Simultaneous translation' },
  ];

  const cities = [
    'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata',
    'Pune', 'Ahmedabad', 'Jaipur', 'Goa', 'Udaipur', 'Agra', 'Kochi',
    'Chandigarh', 'Lucknow', 'Indore', 'Coimbatore', 'Visakhapatnam'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => {
      const arr = prev[field];
      if (arr.includes(item)) {
        return { ...prev, [field]: arr.filter(i => i !== item) };
      }
      return { ...prev, [field]: [...arr, item] };
    });
  };

  const handleEventElementChange = (elementKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      event_elements: {
        ...prev.event_elements,
        [elementKey]: {
          ...prev.event_elements[elementKey],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validate required fields before submission
      const validationErrors = [];
      
      if (!formData.company_name?.trim()) validationErrors.push('Company Name is required');
      if (!formData.decision_maker_name?.trim()) validationErrors.push('Decision Maker Name is required');
      if (!formData.decision_maker_email?.trim()) validationErrors.push('Decision Maker Email is required');
      if (!formData.decision_maker_phone?.trim()) validationErrors.push('Decision Maker Phone is required');
      if (!formData.event_name?.trim()) validationErrors.push('Event Name is required');
      if (!formData.event_description?.trim()) validationErrors.push('Event Description is required');
      if (!formData.primary_check_in) validationErrors.push('Primary Check-in Date is required');
      if (!formData.primary_check_out) validationErrors.push('Primary Check-out Date is required');
      if (formData.preferred_cities.length === 0) validationErrors.push('Please select at least one preferred city');
      if (formData.min_attendees < 10) validationErrors.push('Minimum attendees must be at least 10');
      if (formData.max_attendees < formData.min_attendees) validationErrors.push('Maximum attendees must be greater than minimum');
      
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => toast.error(error));
        setIsSubmitting(false);
        return;
      }
      
      // Format dates for backend
      const submissionData = {
        ...formData,
        primary_dates: formData.primary_check_in ? format(formData.primary_check_in, 'yyyy-MM-dd') : '',
        alternate_dates: formData.alternate_check_in ? format(formData.alternate_check_in, 'yyyy-MM-dd') : '',
      };
      
      // Remove the date objects from submission
      delete submissionData.primary_check_in;
      delete submissionData.primary_check_out;
      delete submissionData.alternate_check_in;
      delete submissionData.alternate_check_out;
      
      const response = await api.createBulkRFP(submissionData);
      toast.success('RFP submitted successfully! Hotels will respond within 1 hour.');
      navigate('/dashboard/client/rfps');
    } catch (error) {
      console.error('RFP submission error:', error);
      
      // Provide specific error messages based on error type
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;
        
        if (status === 401) {
          toast.error('Session expired. Please login again to submit your RFP.');
        } else if (status === 400) {
          toast.error(detail || 'Invalid form data. Please check all required fields.');
        } else if (status === 422) {
          // Validation error from backend
          if (typeof detail === 'object' && detail.length > 0) {
            detail.forEach(err => toast.error(`${err.loc?.join(' → ') || 'Field'}: ${err.msg}`));
          } else {
            toast.error(detail || 'Please fill all required fields correctly.');
          }
        } else if (status === 500) {
          toast.error('Server error. Please try again later or contact support.');
        } else {
          toast.error(detail || 'Failed to submit RFP. Please try again.');
        }
      } else if (error.request) {
        toast.error('Network error. Please check your internet connection and try again.');
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Please Login</h2>
          <p className="text-slate-800 mb-4">You need to be logged in as a client to submit an RFP</p>
          <Link to="/"><Button>Go to Login</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/client">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </Link>
            <h1 className="luxury-text text-slate-900 text-xl font-semibold text-midnight-navy">Bulk Booking Request</h1>
          </div>
          <span className="text-sm text-slate-800">Step {currentSection + 1} of {sections.length}</span>
        </div>
      </nav>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {sections.map((section, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSection(idx)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  idx === currentSection
                    ? 'bg-midnight-navy text-white'
                    : idx < currentSection
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-slate-100 text-slate-800'
                }`}
              >
                {idx < currentSection ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <section.icon className="w-4 h-4" />
                )}
                {section.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto px-6 py-8">
        {/* Section 0: Company Information */}
        {currentSection === 0 && (
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold flex items-center gap-2 text-navy">
              <Building2 className="w-5 h-5 text-champagne-gold" />
              Company Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="Enter company name"
                  required
                />
              </div>
              <div>
                <Label>Industry</Label>
                <Select value={formData.company_industry} onValueChange={(v) => handleChange('company_industry', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="finance">Finance & Banking</SelectItem>
                    <SelectItem value="pharma">Pharmaceutical</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="consulting">Consulting</SelectItem>
                    <SelectItem value="fmcg">FMCG</SelectItem>
                    <SelectItem value="automotive">Automotive</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST Number</Label>
                <Input
                  value={formData.gst_number}
                  onChange={(e) => handleChange('gst_number', e.target.value)}
                  placeholder="Enter GST number"
                />
              </div>
              <div>
                <Label>Previous MICE Experience</Label>
                <Select value={formData.previous_mice_experience} onValueChange={(v) => handleChange('previous_mice_experience', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_time">First Time</SelectItem>
                    <SelectItem value="1-3_events">1-3 Events</SelectItem>
                    <SelectItem value="4-10_events">4-10 Events</SelectItem>
                    <SelectItem value="10+_events">10+ Events</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-medium mb-4">Decision Maker Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.decision_maker_name}
                    onChange={(e) => handleChange('decision_maker_name', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.decision_maker_email}
                    onChange={(e) => handleChange('decision_maker_email', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={formData.decision_maker_phone}
                    onChange={(e) => handleChange('decision_maker_phone', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-medium mb-4">Finance Contact (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={formData.finance_contact_name}
                    onChange={(e) => handleChange('finance_contact_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.finance_contact_email}
                    onChange={(e) => handleChange('finance_contact_email', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section 1: Event Details */}
        {currentSection === 1 && (
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-champagne-gold" />
              Event Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Event Name *</Label>
                <Input
                  value={formData.event_name}
                  onChange={(e) => handleChange('event_name', e.target.value)}
                  placeholder="Annual Sales Conference 2025"
                  required
                />
              </div>
              <div>
                <Label>Event Type *</Label>
                <Select value={formData.event_type} onValueChange={(v) => handleChange('event_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conference">Conference</SelectItem>
                    <SelectItem value="offsite">Corporate Offsite</SelectItem>
                    <SelectItem value="product_launch">Product Launch</SelectItem>
                    <SelectItem value="training">Training/Workshop</SelectItem>
                    <SelectItem value="team_building">Team Building</SelectItem>
                    <SelectItem value="exhibition">Exhibition</SelectItem>
                    <SelectItem value="award_ceremony">Award Ceremony</SelectItem>
                    <SelectItem value="annual_meet">Annual Meet</SelectItem>
                    <SelectItem value="dealer_meet">Dealer/Distributor Meet</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Event Description *</Label>
              <Textarea
                value={formData.event_description}
                onChange={(e) => handleChange('event_description', e.target.value)}
                placeholder="Describe your event objectives, key activities, and any special requirements..."
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label>Min Attendees *</Label>
                <Input
                  type="number"
                  value={formData.min_attendees}
                  onChange={(e) => handleChange('min_attendees', parseInt(e.target.value))}
                  min={10}
                  required
                />
              </div>
              <div>
                <Label>Max Attendees *</Label>
                <Input
                  type="number"
                  value={formData.max_attendees}
                  onChange={(e) => handleChange('max_attendees', parseInt(e.target.value))}
                  min={formData.min_attendees}
                  required
                />
              </div>
              <div>
                <Label>Event Duration (Days) *</Label>
                <Input
                  type="number"
                  value={formData.event_duration_days}
                  onChange={(e) => handleChange('event_duration_days', parseInt(e.target.value))}
                  min={1}
                  max={30}
                  required
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-base font-semibold text-slate-900 mb-3 block">Primary Dates *</Label>
                <DateRangePicker
                  checkInDate={formData.primary_check_in}
                  checkOutDate={formData.primary_check_out}
                  onCheckInChange={(date) => handleChange('primary_check_in', date)}
                  onCheckOutChange={(date) => handleChange('primary_check_out', date)}
                />
              </div>
              <div>
                <Label className="text-base font-semibold text-slate-900 mb-3 block">Alternate Dates (Optional)</Label>
                <DateRangePicker
                  checkInDate={formData.alternate_check_in}
                  checkOutDate={formData.alternate_check_out}
                  onCheckInChange={(date) => handleChange('alternate_check_in', date)}
                  onCheckOutChange={(date) => handleChange('alternate_check_out', date)}
                />
              </div>
            </div>

            <div>
              <Label>Preferred Cities (Select multiple) *</Label>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-2">
                {cities.map(city => (
                  <label key={city} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.preferred_cities.includes(city)}
                      onCheckedChange={() => toggleArrayItem('preferred_cities', city)}
                    />
                    <span className="text-sm">{city}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.is_recurring_event}
                  onCheckedChange={(checked) => handleChange('is_recurring_event', checked)}
                />
                <span>This is a recurring event</span>
              </label>
              {formData.is_recurring_event && (
                <Select value={formData.recurring_frequency} onValueChange={(v) => handleChange('recurring_frequency', v)}>
                  <SelectTrigger className="w-48"><SelectValue placeholder="Frequency" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="half_yearly">Half Yearly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        )}

        {/* Section 2: Accommodation */}
        {currentSection === 2 && (
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-champagne-gold" />
              Accommodation Requirements
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Total Rooms Required *</Label>
                <Input
                  type="number"
                  value={formData.total_rooms_required}
                  onChange={(e) => handleChange('total_rooms_required', parseInt(e.target.value))}
                  min={1}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Room Category Breakdown</Label>
              <div className="space-y-3 mt-2">
                {['Standard', 'Deluxe', 'Suite', 'Executive Suite'].map(category => (
                  <div key={category} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <span className="w-32 font-medium">{category}</span>
                    <Input
                      type="number"
                      placeholder="Rooms"
                      className="w-24"
                      min={0}
                    />
                    <Select defaultValue="double">
                      <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single Occupancy</SelectItem>
                        <SelectItem value="double">Double Occupancy</SelectItem>
                        <SelectItem value="triple">Triple Occupancy</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.early_checkin_required}
                  onCheckedChange={(checked) => handleChange('early_checkin_required', checked)}
                />
                <span>Early Check-in Required</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.late_checkout_required}
                  onCheckedChange={(checked) => handleChange('late_checkout_required', checked)}
                />
                <span>Late Check-out Required</span>
              </label>
            </div>

            <div>
              <Label>Special Room Requirements</Label>
              <Textarea
                value={formData.special_room_requirements}
                onChange={(e) => handleChange('special_room_requirements', e.target.value)}
                placeholder="E.g., Wheelchair accessible rooms, connecting rooms for families, specific floor preferences..."
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Section 3: Venue */}
        {currentSection === 3 && (
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-champagne-gold" />
              Venue Requirements
            </h2>
            
            <div>
              <Label>Session Types Required *</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {['plenary', 'breakout', 'workshop', 'exhibition', 'networking', 'registration', 'vip_lounge'].map(type => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      checked={formData.session_types.includes(type)}
                      onCheckedChange={() => toggleArrayItem('session_types', type)}
                    />
                    <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label>Main Venue Capacity *</Label>
                <Input
                  type="number"
                  value={formData.main_venue_capacity}
                  onChange={(e) => handleChange('main_venue_capacity', parseInt(e.target.value))}
                  min={10}
                  required
                />
              </div>
              <div>
                <Label>Seating Arrangement</Label>
                <Select value={formData.seating_arrangement} onValueChange={(v) => handleChange('seating_arrangement', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="theatre">Theatre Style</SelectItem>
                    <SelectItem value="classroom">Classroom</SelectItem>
                    <SelectItem value="u_shape">U-Shape</SelectItem>
                    <SelectItem value="banquet">Banquet</SelectItem>
                    <SelectItem value="cluster">Cluster Seating</SelectItem>
                    <SelectItem value="cocktail">Cocktail/Standing</SelectItem>
                    <SelectItem value="boardroom">Boardroom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Breakout Rooms Needed</Label>
                <Input
                  type="number"
                  value={formData.breakout_rooms_count}
                  onChange={(e) => handleChange('breakout_rooms_count', parseInt(e.target.value))}
                  min={0}
                />
              </div>
            </div>

            <div>
              <Label>AV Equipment Required</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {['projector', 'screen', 'microphones', 'video_conf', 'live_streaming', 'led_wall', 'teleprompter', 'recording'].map(item => (
                  <label key={item} className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      checked={formData.av_requirements.includes(item)}
                      onCheckedChange={() => toggleArrayItem('av_requirements', item)}
                    />
                    <span className="text-sm capitalize">{item.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.stage_required}
                  onCheckedChange={(checked) => handleChange('stage_required', checked)}
                />
                <span>Stage/Podium Required</span>
              </label>
              <div>
                <Label>Exhibition Space (sq.ft)</Label>
                <Input
                  type="number"
                  value={formData.exhibition_space_sqft}
                  onChange={(e) => handleChange('exhibition_space_sqft', parseInt(e.target.value))}
                  min={0}
                />
              </div>
            </div>
          </div>
        )}

        {/* Section 4: Event Elements Sheet */}
        {currentSection === 4 && (
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-champagne-gold" />
              Event Elements Sheet
            </h2>
            <p className="text-slate-800 text-sm">
              Select the production elements you need for your event. Specify requirements and budget for each.
            </p>
            
            <div className="space-y-4">
              {eventElementsList.map((element) => (
                <div 
                  key={element.key} 
                  className={`border rounded-xl p-4 transition-all ${
                    formData.event_elements[element.key]?.required 
                      ? 'border-gold bg-gold/5' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{element.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-slate-900">{element.label}</h3>
                          <p className="text-sm text-slate-700">{element.desc}</p>
                        </div>
                        <Checkbox
                          checked={formData.event_elements[element.key]?.required || false}
                          onCheckedChange={(checked) => handleEventElementChange(element.key, 'required', checked)}
                        />
                      </div>
                      
                      {formData.event_elements[element.key]?.required && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200">
                          <div>
                            <Label className="text-sm text-slate-700">Specifications / Requirements</Label>
                            <Textarea
                              placeholder={`E.g., ${element.desc}`}
                              value={formData.event_elements[element.key]?.specs || ''}
                              onChange={(e) => handleEventElementChange(element.key, 'specs', e.target.value)}
                              className="mt-1"
                              rows={2}
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-slate-700">Budget Range (₹)</Label>
                            <Select 
                              value={formData.event_elements[element.key]?.budget || ''} 
                              onValueChange={(v) => handleEventElementChange(element.key, 'budget', v)}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select budget range" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="under_25k">Under ₹25,000</SelectItem>
                                <SelectItem value="25k_50k">₹25,000 - ₹50,000</SelectItem>
                                <SelectItem value="50k_1l">₹50,000 - ₹1,00,000</SelectItem>
                                <SelectItem value="1l_2l">₹1,00,000 - ₹2,00,000</SelectItem>
                                <SelectItem value="2l_5l">₹2,00,000 - ₹5,00,000</SelectItem>
                                <SelectItem value="above_5l">Above ₹5,00,000</SelectItem>
                                <SelectItem value="included">Include in total budget</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-800">
                <strong>Tip:</strong> Being specific about your requirements helps hotels provide accurate quotes. 
                Include details like screen sizes, speaker counts, branding dimensions, etc.
              </p>
            </div>
          </div>
        )}

        {/* Section 5: F&B */}
        {currentSection === 5 && (
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Utensils className="w-5 h-5 text-champagne-gold" />
              Food & Beverage Requirements
            </h2>
            
            <div>
              <Label>Meal Plans Required *</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
                {['breakfast', 'lunch', 'dinner', 'hi_tea', 'cocktails', 'working_lunch', 'gala_dinner'].map(meal => (
                  <label key={meal} className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      checked={formData.meal_plans.includes(meal)}
                      onCheckedChange={() => toggleArrayItem('meal_plans', meal)}
                    />
                    <span className="text-sm capitalize">{meal.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Cuisine Preferences</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {['indian', 'continental', 'asian', 'italian', 'mexican', 'mediterranean', 'local_regional'].map(cuisine => (
                  <label key={cuisine} className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      checked={formData.cuisine_preferences.includes(cuisine)}
                      onCheckedChange={() => toggleArrayItem('cuisine_preferences', cuisine)}
                    />
                    <span className="text-sm capitalize">{cuisine.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Vegetarian Percentage (%)</Label>
                <Input
                  type="number"
                  value={formData.vegetarian_percentage}
                  onChange={(e) => handleChange('vegetarian_percentage', parseInt(e.target.value))}
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <Label>Special Dietary Needs</Label>
                <Input
                  value={formData.special_dietary_needs}
                  onChange={(e) => handleChange('special_dietary_needs', e.target.value)}
                  placeholder="Jain, Halal, Gluten-free, Vegan..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.cocktail_dinner_required}
                  onCheckedChange={(checked) => handleChange('cocktail_dinner_required', checked)}
                />
                <span>Cocktail/Gala Dinner Required</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.outside_catering_allowed}
                  onCheckedChange={(checked) => handleChange('outside_catering_allowed', checked)}
                />
                <span>Outside Catering Consideration</span>
              </label>
            </div>
          </div>
        )}

        {/* Section 6: Budget */}
        {currentSection === 6 && (
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-champagne-gold" />
              Budget & Payment
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Minimum Budget (₹) *</Label>
                <Input
                  type="number"
                  value={formData.total_budget_min}
                  onChange={(e) => handleChange('total_budget_min', parseFloat(e.target.value))}
                  min={100000}
                  step={50000}
                  required
                />
              </div>
              <div>
                <Label>Maximum Budget (₹) *</Label>
                <Input
                  type="number"
                  value={formData.total_budget_max}
                  onChange={(e) => handleChange('total_budget_max', parseFloat(e.target.value))}
                  min={formData.total_budget_min}
                  step={50000}
                  required
                />
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-800 mb-2">Budget Range:</p>
              <p className="text-2xl font-semibold text-midnight-navy">
                ₹{formData.total_budget_min.toLocaleString()} - ₹{formData.total_budget_max.toLocaleString()}
              </p>
              <p className="text-sm text-slate-800 mt-1">
                Per person: ~₹{Math.round(formData.total_budget_min / formData.max_attendees).toLocaleString()} - 
                ₹{Math.round(formData.total_budget_max / formData.max_attendees).toLocaleString()}
              </p>
            </div>

            <div>
              <Label>Payment Terms Preference</Label>
              <Select value={formData.payment_terms_preference} onValueChange={(v) => handleChange('payment_terms_preference', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="advance_100">100% Advance</SelectItem>
                  <SelectItem value="advance_50_balance_before">50% Advance, Balance Before Event</SelectItem>
                  <SelectItem value="milestone">Milestone Based</SelectItem>
                  <SelectItem value="post_event_30_days">Post Event (30 Days Credit)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg">
              <Checkbox
                checked={formData.need_installment_plan}
                onCheckedChange={(checked) => handleChange('need_installment_plan', checked)}
              />
              <span>Need Installment Payment Plan</span>
            </label>
          </div>
        )}

        {/* Section 7: Additional Services */}
        {currentSection === 7 && (
          <div className="bg-white rounded-lg border p-6 space-y-6">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-champagne-gold" />
              Additional Services
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'airport_transfers_required', label: 'Airport Transfers' },
                { key: 'team_building_activities', label: 'Team Building Activities' },
                { key: 'spa_wellness_packages', label: 'Spa & Wellness Packages' },
                { key: 'gifts_welcome_kits', label: 'Gifts / Welcome Kits' },
                { key: 'photography_videography', label: 'Photography & Videography' },
                { key: 'entertainment_required', label: 'Entertainment / DJ' },
              ].map(item => (
                <label key={item.key} className="flex items-center gap-2 cursor-pointer p-4 bg-slate-50 rounded-lg hover:bg-slate-100">
                  <Checkbox
                    checked={formData[item.key]}
                    onCheckedChange={(checked) => handleChange(item.key, checked)}
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>

            <div>
              <Label>Special Requests / Additional Notes</Label>
              <Textarea
                value={formData.special_requests}
                onChange={(e) => handleChange('special_requests', e.target.value)}
                placeholder="Any other specific requirements or preferences for your event..."
                rows={5}
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-800 mb-2">⏰ What happens next?</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Your RFP will be broadcast to all matching hotels in your preferred cities</li>
                <li>• Hotels will submit their pitches within <strong>1 hour</strong></li>
                <li>• You&apos;ll be able to compare quotes and select the best offer</li>
                <li>• Corporate email domains get auto-verified for faster processing</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
            disabled={currentSection === 0}
          >
            Previous
          </Button>
          
          {currentSection < sections.length - 1 ? (
            <Button
              type="button"
              onClick={() => setCurrentSection(prev => prev + 1)}
              className="bg-midnight-navy"
            >
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isSubmitting || formData.preferred_cities.length === 0}
              className="bg-champagne-gold text-midnight-navy hover:bg-champagne-gold/90"
            >
              {isSubmitting ? 'Submitting...' : 'Submit RFP'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
